import React, { FC, useRef } from 'react'
import { Layout, Nav, Button, Dropdown, RadioGroup, Radio, Popover, Empty, Tag, Space } from '@douyinfe/semi-ui'
import Breadcrumb from '@src/pages/layout/components/breadcrumb'
import { useNavigate } from 'react-router'
import { loginOut } from '@src/api/user/login'
import { useIntl } from 'react-intl'
import { selectLocale, switchMode } from '@src/common'
import { IconColorPlatte, IconDarkMode, IconList } from '@douyinfe/semi-icons-lab'
import { IconGithubLogo, IconHome, IconSearch } from '@douyinfe/semi-icons'
import useGlobalStore from '@src/store/common/global'
import userStore from '@src/store/common/user'
import { fmtDateTime } from '@src/utils/date'

import '@src/pages/layout/index.scss'
import OneTable, { OneTableRef } from '@components/OneTable'
import { getTaskList } from '@src/api/sys/task'
import codeService from '@src/services/codeService'
import { useGlobalSearchStore } from '@src/components/GlobalSearch'
import HeaderMessage from '@components/HeaderMessage'

const { Header } = Layout

const Index: FC = () => {
	const navigate = useNavigate()
	const locale = useGlobalStore((state) => state.locale)
	const logout = userStore((state) => state.logout)
	const { formatMessage: i18nMsg } = useIntl()
	const openGlobalSearch = useGlobalSearchStore((s) => s.setOpen)
	const selectDropdown = (drop: 'user_center' | 'user_setting' | 'logout') => {
		if (drop == 'user_center') {
			navigate('/user/center')
		} else if (drop == 'user_setting') {
			navigate('/user/settings')
		} else {
			loginOut()
				.then((res) => {
					logout()
					codeService.clearCodes()
					console.log('logout', res)
					navigate('/login')
				})
				.catch((err) => {
					console.log('logout err', err)
				})
		}
	}
	const tableApi = useRef<OneTableRef>(null)

	/**
	 * 异步任务队列
	 */
	const asyncTaskColumns = [
		{
			title: '任务ID',
			dataIndex: 'id',
			align: 'center' as const,
			render: (text) => `${text}`
		},
		{
			title: '任务状态',
			dataIndex: 'taskSatus',
			align: 'center' as const,
			render: (text) => `${text}`
		},
		{
			title: '任务名称',
			dataIndex: 'taskName',
			align: 'center' as const,
			render: (text) => `${text}`
		},
		{
			title: i18nMsg({ id: 'app.system.common.create.time' }),
			dataIndex: 'createTime',
			align: 'center' as const,
			render: (value) => {
				return fmtDateTime(value)
			}
		},
		{
			title: i18nMsg({ id: 'app.system.common.operator' }),
			dataIndex: 'operate',
			fixed: 'right' as const,
			align: 'center' as const,
			render: (text, record, index) => {
				return (
					<Space>
						<Button type={'danger'} onClick={() => console.log('')}>
							{i18nMsg({ id: 'app.system.common.button.delete' })}
						</Button>
					</Space>
				)
			}
		}
	]
	return (
		<Header className="layout-header">
			<Nav
				mode="horizontal"
				header={<Breadcrumb />}
				footer={
					<>
						{/* 顶部全局搜索触发器（fake input，点击或 Cmd+K 唤起） */}
						<div
							role="button"
							tabIndex={0}
							onClick={() => openGlobalSearch(true)}
							onKeyDown={(e) => {
								if (e.key === 'Enter' || e.key === ' ') {
									e.preventDefault()
									openGlobalSearch(true)
								}
							}}
							style={{
								display: 'inline-flex',
								alignItems: 'center',
								gap: 8,
								padding: '0 12px',
								height: 32,
								minWidth: 220,
								borderRadius: 4,
								background: 'var(--semi-color-fill-1)',
								color: 'var(--semi-color-text-2)',
								cursor: 'text',
								marginRight: '12px',
								userSelect: 'none'
							}}
						>
							<IconSearch size="small" />
							<span style={{ flex: 1, fontSize: 13 }}>{i18nMsg({ id: 'app.common.search.placeholder' })}</span>
							<span
								style={{
									fontSize: 11,
									padding: '1px 6px',
									borderRadius: 3,
									background: 'var(--semi-color-bg-2)',
									border: '1px solid var(--semi-color-border)',
									color: 'var(--semi-color-text-2)',
									fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace'
								}}
							>
								⌘K
							</span>
						</div>
						<Button
							theme="borderless"
							icon={<IconHome size="extra-large" />}
							className="header-link-external"
							style={{
								color: 'var(--semi-color-text-2)',
								marginRight: '12px'
							}}
							onClick={() => window.open('http://plus-one.cn', '_blank')}
						/>
						<Button
							theme="borderless"
							icon={<IconGithubLogo size="extra-large" />}
							className="header-link-external"
							style={{
								color: 'var(--semi-color-text-2)',
								marginRight: '12px'
							}}
							onClick={() => window.open('https://gitee.com/lkShi/plus-one-ui', '_blank')}
						/>
						<Popover
							onVisibleChange={(visible) => {
								if (visible) {
									console.log('visible', visible, tableApi)
									tableApi.current?.refresh({ dto: {} })
								}
							}}
							content={
								<div style={{ width: '600px', borderRadius: '20px' }}>
									<OneTable ref={tableApi} refreshApi={getTaskList} columns={asyncTaskColumns} />
								</div>
							}
						>
							<Button
								theme="borderless"
								icon={<IconList size="extra-large" />}
								style={{
									color: 'var(--semi-color-black)',
									marginRight: '12px'
								}}
							/>
						</Popover>
						<HeaderMessage />
						<Button
							theme="borderless"
							icon={<IconDarkMode size="extra-large" />}
							style={{
								color: 'var(--semi-color-text-2)',
								marginRight: '12px'
							}}
							onClick={(e: React.MouseEvent) => switchMode(e.clientX, e.clientY)}
						/>
						<Button
							theme="borderless"
							icon={<IconColorPlatte size="extra-large" />}
							style={{
								color: 'var(--semi-color-text-2)',
								marginRight: '12px'
							}}
							onClick={() => useGlobalStore.getState().setThemeDrawerOpen(true)}
						/>

						<Dropdown
							render={
								<Dropdown.Menu>
									<Dropdown.Item onClick={() => selectDropdown('user_center')}>
										{i18nMsg({ id: 'app.system.common.user.center' })}
									</Dropdown.Item>
									<Dropdown.Item onClick={() => selectDropdown('user_setting')}>
										{i18nMsg({ id: 'app.system.common.user.setting' })}
									</Dropdown.Item>
									<Dropdown.Item onClick={() => selectDropdown('logout')}>
										{i18nMsg({ id: 'app.system.common.user.logout' })}
									</Dropdown.Item>
								</Dropdown.Menu>
							}
						>
							<span className="user-header-avatar" aria-label={userStore.getState().name}>
								{userStore.getState().name[0]}
							</span>
						</Dropdown>
						<RadioGroup
							type="button"
							defaultValue={locale}
							style={{ marginLeft: '20px' }}
							className="header-locale"
						>
							<Radio value={'zh_CN'} onChange={() => selectLocale('zh_CN')}>
								{i18nMsg({ id: 'app.common.language.chinese' })}
							</Radio>
							<Radio value={'en_GB'} onChange={() => selectLocale('en_GB')}>
								EN
							</Radio>
						</RadioGroup>
					</>
				}
			/>
		</Header>
	)
}

export default Index
